// Per-scenario placeholder hints + ready-to-load example scenarios.
// Examples double as demo + test data. Click an example to fill all wizard fields.

const empty = {
  stakeholders: "",
  pressures: "",
  assumptions: "",
  timeline: "",
  desired_outcome: "",
};

export const SCENARIO_DATA = {
  "M&A": {
    prompt_placeholder:
      "e.g. Should we counter the acquirer's $40M all-cash offer with a structured earn-out, or hold the line?",
    context_placeholders: {
      stakeholders: "e.g. CEO, founders, acquirer CFO, board, lead investor",
      pressures: "e.g. 10-day closing window, cap-table sensitivity, key talent retention",
      assumptions: "e.g. acquirer needs us more than they're showing",
      timeline: "e.g. signed LOI in 7 days",
      desired_outcome: "e.g. protect founder upside without losing the deal",
    },
    examples: [
      {
        title: "Earn-out vs all-cash",
        prompt:
          "Should we counter the acquirer's $40M all-cash offer with a structured earn-out worth potentially $55M, or hold the line on cash?",
        context: {
          stakeholders: "CEO, two founders, acquirer CFO, our board chair, lead VC",
          pressures: "10-day closing window, two key engineers considering offers elsewhere",
          assumptions: "Acquirer's strategic need for our product is higher than their offer suggests",
          timeline: "Decision needed within 7 days",
          desired_outcome: "Protect founder upside; preserve team continuity post-close",
        },
      },
      {
        title: "Run a process vs sign warm",
        prompt:
          "Two strategic acquirers expressed interest within a week. Do we run a structured process to maximise price, or sign with the warmer one before momentum fades?",
        context: {
          stakeholders: "Founders, board, banker, both acquirers' corp-dev leads",
          pressures: "Q4 budget cycles closing, our own runway is 9 months",
          assumptions: "A process would unlock 20–30% more value but adds 60–90 days",
          timeline: "Warmer acquirer wants exclusivity decision in 14 days",
          desired_outcome: "Best risk-adjusted outcome, not just highest headline number",
        },
      },
      {
        title: "Walk away or push",
        prompt:
          "The current offer undervalues us by ~30% versus our internal model. Do we walk away from the sale entirely, or push for a higher number and risk losing the deal?",
        context: {
          stakeholders: "Founders, board, key employees with vested equity",
          pressures: "Market sentiment cooling, no other live offers",
          assumptions: "Our model may be optimistic on next-year ARR by ~15%",
          timeline: "Response expected within 5 business days",
          desired_outcome: "Either a fair number now or a credible path to a fair number later",
        },
      },
    ],
  },

  "Negotiation": {
    prompt_placeholder:
      "e.g. A key supplier raised prices 22%. Do we accept, dual-source, or absorb and re-price downstream?",
    context_placeholders: {
      stakeholders: "e.g. supplier exec, our COO, finance lead, key customer",
      pressures: "e.g. existing contract expires in 30 days, no obvious second source",
      assumptions: "e.g. they raised because they think we have no alternative",
      timeline: "e.g. new pricing kicks in next quarter",
      desired_outcome: "e.g. cap the increase under 10% without breaking the relationship",
    },
    examples: [
      {
        title: "Supplier price hike",
        prompt:
          "Our anchor supplier announced a 22% price increase. Do we accept and pass it through, dual-source quickly, or absorb part of it to keep customer prices stable?",
        context: {
          stakeholders: "Supplier account exec, our COO, CFO, top 5 customers",
          pressures: "Contract auto-renews in 45 days; switching cost ~$120k",
          assumptions: "Supplier believes we have no live alternative",
          timeline: "Renewal decision in 30 days",
          desired_outcome: "Cap the increase at 8% while signalling we will dual-source if pushed",
        },
      },
      {
        title: "JV split 60/40",
        prompt:
          "Our counter-party in a joint venture is insisting on 60/40 in their favour. Do we concede for speed, hold for 50/50, or restructure the deal as a licensing arrangement?",
        context: {
          stakeholders: "Our CEO + GC, their EVP corp-dev, both boards",
          pressures: "Public announcement targeted within the quarter",
          assumptions: "They have a competing partner waiting in the wings",
          timeline: "Term sheet due in 21 days",
          desired_outcome: "50/50 control, asymmetric upside on the IP we contribute",
        },
      },
      {
        title: "Exclusivity for commitment",
        prompt:
          "Anchor partner asked for exclusivity in our category in exchange for a 3-year commit at 2.5x current volume. Say yes, narrow the scope, or decline?",
        context: {
          stakeholders: "Partner head of partnerships, our CRO, key alternative-channel customers",
          pressures: "Sales pipeline visibility for next year, board pressure for ARR predictability",
          assumptions: "Exclusivity would close ~$8M in pipeline outside this partner",
          timeline: "Verbal answer expected in 10 days",
          desired_outcome: "Predictable revenue without closing the door on other channels",
        },
      },
    ],
  },

  "Fundraising": {
    prompt_placeholder:
      "e.g. Take an aggressive lead-led $8M at a flat valuation, or wait 6 months for a better number with thinner runway?",
    context_placeholders: {
      stakeholders: "e.g. founders, board, lead investor, existing pro-rata investors",
      pressures: "e.g. 9-month runway, market signals softening",
      assumptions: "e.g. valuations recover in H2",
      timeline: "e.g. need cash in 60 days at the latest",
      desired_outcome: "e.g. minimum dilution without taking irresponsible runway risk",
    },
    examples: [
      {
        title: "Flat now vs better later",
        prompt:
          "We can take a $8M lead-led Series A at a flat valuation today, or wait 6 months when we'll have stronger numbers — but with thinner runway and uncertain market.",
        context: {
          stakeholders: "Founders, board, lead investor, existing pro-rata holders",
          pressures: "9 months runway, market signals softening, team morale watching closely",
          assumptions: "Valuations recover modestly in H2; ARR growth holds at 8% MoM",
          timeline: "Lead investor wants term sheet signed within 21 days",
          desired_outcome: "Strong balance sheet without permanently anchoring a low valuation",
        },
      },
      {
        title: "Strategic with strings",
        prompt:
          "A strategic offered to lead our Series A, but they want a board seat, ROFR on a future sale, and a 12-month exclusivity on partnership. Accept, push back, or replace them as co-investor?",
        context: {
          stakeholders: "Founders, current board, strategic's corp-dev VP, alt VC lead",
          pressures: "Alt VC moving slower; closing window in ~30 days",
          assumptions: "Strategic's ROFR will scare future acquirers",
          timeline: "Term sheet decision in 14 days",
          desired_outcome: "Capital + signal without compromising future M&A optionality",
        },
      },
      {
        title: "Bridge or wait",
        prompt:
          "Existing investor offered a $2M convertible bridge at a favourable cap. Take it to extend runway, or hold out for a priced round in Q2?",
        context: {
          stakeholders: "Founders, lead existing investor, other note-holders",
          pressures: "Runway shrinking, hiring plan paused",
          assumptions: "Q2 metrics will support a meaningfully higher cap",
          timeline: "Bridge offer expires in 14 days",
          desired_outcome: "Avoid a down round; keep optionality on lead investor in Q2",
        },
      },
    ],
  },

  "Sales": {
    prompt_placeholder:
      "e.g. Major prospect wants a 35% custom discount + 90-day pilot. Accept, counter, or walk?",
    context_placeholders: {
      stakeholders: "e.g. prospect VP, our AE + CRO, finance, existing customer base",
      pressures: "e.g. quarter-end, this deal swings 18% of quota",
      assumptions: "e.g. they will eventually pay full price after pilot",
      timeline: "e.g. decision required this week",
      desired_outcome: "e.g. close without setting a precedent that hurts other deals",
    },
    examples: [
      {
        title: "Big logo, big discount",
        prompt:
          "A flagship enterprise prospect wants a 35% custom discount plus a 90-day free pilot. Do we accept, counter at 15% + a paid pilot, or walk?",
        context: {
          stakeholders: "Prospect's VP Engineering + procurement, our AE, CRO, CFO",
          pressures: "Quarter-end, this deal swings 18% of quota; we have one similar customer asking for parity",
          assumptions: "They will renew at standard pricing after the pilot",
          timeline: "Decision needed this week",
          desired_outcome: "Close the logo without resetting our discount baseline",
        },
      },
      {
        title: "Renewal price hike",
        prompt:
          "Top-3 customer's renewal is up. The plan is to raise price 18% in line with new pricing. Do we hold the increase to protect relationship, or apply it?",
        context: {
          stakeholders: "Customer COO + procurement, our CSM, CRO, head of pricing",
          pressures: "Their budget cycle just closed; they were not pre-warned",
          assumptions: "They have one weaker alternative they could test",
          timeline: "Renewal date in 45 days",
          desired_outcome: "Capture meaningful uplift without triggering an RFP",
        },
      },
      {
        title: "Hold price or lose",
        prompt:
          "Competitor is undercutting us by 28% in a head-to-head deal. Discount hard to win, or hold price and lose this one cleanly?",
        context: {
          stakeholders: "Prospect, our AE, CRO, marketing (case-study value)",
          pressures: "Visible deal — peers in the segment are watching",
          assumptions: "Competitor's pricing isn't sustainable at scale",
          timeline: "Decision in 10 days",
          desired_outcome: "Protect price integrity; only chase if strategic value is real",
        },
      },
    ],
  },

  "Board / leadership": {
    prompt_placeholder:
      "e.g. Two board members publicly disagree with our pricing pivot. Bring it to a formal vote, or build consensus 1:1 first?",
    context_placeholders: {
      stakeholders: "e.g. dissenting board members, chair, exec team, key investors",
      pressures: "e.g. next board meeting in 3 weeks, leadership team waiting on signal",
      assumptions: "e.g. dissent is about framing, not substance",
      timeline: "e.g. pivot launch planned in 6 weeks",
      desired_outcome: "e.g. unified board behind the pivot, even if reluctantly",
    },
    examples: [
      {
        title: "Public board dissent",
        prompt:
          "Two board members are pushing back hard on our pricing pivot — one in the last meeting, one in writing. Do we bring it to a formal vote, or build consensus 1:1 before the next meeting?",
        context: {
          stakeholders: "Chair, dissenting board members, CEO, CRO, lead investor",
          pressures: "Next board meeting in 3 weeks; pricing launch in 6 weeks",
          assumptions: "Their concern is about framing and risk, not the underlying strategy",
          timeline: "Need clarity before all-hands in 4 weeks",
          desired_outcome: "Unified board behind the pivot — even if some are reluctant",
        },
      },
      {
        title: "Outgrown VP",
        prompt:
          "Long-tenured VP whose function has outgrown them. Full exit with a generous package, lateral move into a new role, or restructure around them?",
        context: {
          stakeholders: "VP, their direct reports, CEO, head of People, board",
          pressures: "Two reports may quit if not resolved; quarterly planning starts soon",
          assumptions: "VP knows they're stretched but won't initiate exit",
          timeline: "Resolution needed within 30 days",
          desired_outcome: "Right structure for the next chapter, with dignity for the VP",
        },
      },
      {
        title: "Succession nudge",
        prompt:
          "Chair raised CEO succession quietly. Do we name an internal heir, start an external search, or defer for 12 months?",
        context: {
          stakeholders: "Chair, current CEO, possible internal candidates, key investors",
          pressures: "Investor relations and customer trust both watch this signal",
          assumptions: "Chair is testing, not pushing",
          timeline: "Board wants direction within 60 days",
          desired_outcome: "A credible succession path that doesn't destabilise the present",
        },
      },
    ],
  },

  "Hiring / team": {
    prompt_placeholder:
      "e.g. Star senior engineer asked for 30% above band and an unusual title. Accept, counter, or pass?",
    context_placeholders: {
      stakeholders: "e.g. candidate, hiring manager, head of People, existing team",
      pressures: "e.g. role open for 4 months, two reports want a senior leader",
      assumptions: "e.g. they have a competing offer at the higher number",
      timeline: "e.g. they want a decision in 5 days",
      desired_outcome: "e.g. hire them without breaking compensation equity",
    },
    examples: [
      {
        title: "Above-band star",
        prompt:
          "Star senior engineer is asking for 30% above band, plus an unusual 'Principal' title we don't formally have. Accept, counter, or pass?",
        context: {
          stakeholders: "Candidate, CTO, head of People, existing senior engineers",
          pressures: "Role open 4 months; two reports asked for a senior lead by Q2",
          assumptions: "Candidate has a competing offer at the higher number",
          timeline: "Decision in 5 days",
          desired_outcome: "Hire them without breaking pay equity or creating title creep",
        },
      },
      {
        title: "Internal promote vs external",
        prompt:
          "Two strong head-of-product candidates: a high-potential internal lead, or an external hire with a clear operator track record. Promote internally or hire external?",
        context: {
          stakeholders: "CEO, both candidates, current product team, board",
          pressures: "Team morale watching closely; roadmap commitments slipping",
          assumptions: "Internal could grow into the role in 6 months; external can deliver in 60 days",
          timeline: "Need a head of product within 30 days",
          desired_outcome: "Right leader for the next stage — without losing the internal candidate",
        },
      },
      {
        title: "Popular underperformer",
        prompt:
          "A popular but consistently underperforming team lead. Performance manage out, or restructure around them with a different role?",
        context: {
          stakeholders: "The lead, their team, head of People, CEO",
          pressures: "Quarterly performance review in 30 days; team morale is mixed",
          assumptions: "Their popularity will outlast their exit if handled with care",
          timeline: "Decision before next review cycle",
          desired_outcome: "Right outcome for performance and culture, with respect for the person",
        },
      },
    ],
  },

  "Something else": {
    prompt_placeholder:
      "e.g. Open a second office in a new market 9 months earlier than planned, or stay focused and grow density?",
    context_placeholders: {
      stakeholders: "e.g. leadership team, board, key local hire candidate",
      pressures: "e.g. competitor just announced expansion to the same market",
      assumptions: "e.g. we can hire a strong local lead within 60 days",
      timeline: "e.g. decision needed before next planning cycle",
      desired_outcome: "e.g. growth without diluting focus or culture",
    },
    examples: [
      {
        title: "Second office, earlier",
        prompt:
          "Should we open a second office in a new market 9 months earlier than planned, or stay focused on growing density in our current market?",
        context: {
          stakeholders: "Exec team, board, prospective local lead, current team",
          pressures: "Competitor just announced expansion to the same market",
          assumptions: "We can hire a strong local lead within 60 days",
          timeline: "Decision before next quarterly planning",
          desired_outcome: "Earn a foothold without diluting current-market momentum",
        },
      },
      {
        title: "Public stance",
        prompt:
          "A politically charged industry issue is in the news. Do we make a public statement, stay quiet, or speak privately to peers?",
        context: {
          stakeholders: "Founders, exec team, communications lead, key customers",
          pressures: "Customer base is split; team is asking for a position",
          assumptions: "Silence will be read as a position regardless",
          timeline: "Within 7 days while the story is live",
          desired_outcome: "Authentic position that holds up six months from now",
        },
      },
      {
        title: "Burn cut: depth or breadth",
        prompt:
          "To get to 18 months of runway we need a 22% headcount reduction. Cut deep in fewer functions, broad across many, or a hybrid split?",
        context: {
          stakeholders: "Exec team, board, all employees, key customers",
          pressures: "Runway visibility for next fundraise; team trust",
          assumptions: "Some functions could be paused, not cut, and rebuilt later",
          timeline: "Announce within 30 days, complete in 60",
          desired_outcome: "Survive cleanly without losing the people who carry the next chapter",
        },
      },
    ],
  },
};

export const DEFAULT_SCENARIO = "M&A";

export function getScenario(name) {
  return SCENARIO_DATA[name] || { prompt_placeholder: "", context_placeholders: empty, examples: [] };
}
